import { ThemeProvider } from "@mui/material/styles";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { BrowserRouter as Router } from "react-router-dom";
import { NavBar } from "./components/navbar";
import { IndexRouter } from "./setup/routes/routes";
import { ScrollToTop } from "./components/common/scrollToTop";
import theme from "./styles/common/theme/theme";
import { useEffect, useState } from "react";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { remoteConfig } from "./firebase/firebase";
import AlertSystem from "./components/common/AlertSystem/AlertSystem";
import { isStaff, canAct, getStaffTier } from "./setup/auth/oidc";
import { isLoggedIn, logOut } from "./setup/context/authContext";
import { CapabilitiesProvider } from "./setup/context/capabilitiesContext";
import DenyAccess from "./components/denyAccess";
import Button from "@mui/material/Button";

// Create rtl cache
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

export const App = () => {
  // Staff authorization: the signed-in user must carry one of the 6 admin-web
  // waffy-staff realm roles (staff-admin, staff-admin-finance/ops/settle,
  // staff-admin-viewer, staff-admin-cs) issued by the waffy-staff KC realm via
  // Google Workspace SSO. The OIDC user is hydrated synchronously in index.tsx
  // (initOidc) before first render, so these reads are correct on load.
  const staffLoggedIn = isLoggedIn();
  const isAuthorized = isStaff();
  const staffCanAct = canAct();
  const tier = getStaffTier();
  const [adminOrgEnabled, setAdminOrgEnabled] = useState<boolean>();

  useEffect(() => {
    const fetchRemoteConfig = async () => {
      try {
        await fetchAndActivate(remoteConfig);
        const adminOrgEnabledRemote = getValue(
          remoteConfig,
          "admin_org_enabled"
        ).asBoolean();
        setAdminOrgEnabled(adminOrgEnabledRemote);
      } catch (error) {
        console.error("Error fetching remote config:", error);
        setAdminOrgEnabled(false); // fallback
      }
    };
    fetchRemoteConfig();
  }, []);

  // A signed-in user who lacks a waffy-staff admin-web role must NOT see the
  // admin app. Render a 403 access-denied page instead.
  const isAuthorizedStaff = staffLoggedIn && isAuthorized;

  function AppContent() {
    if (staffLoggedIn && !isAuthorizedStaff) {
      return (
        <CacheProvider value={cacheRtl}>
          <ThemeProvider theme={theme}>
            <div
              dir="rtl"
              style={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "24px",
              }}>
              <DenyAccess />
              {/* Allow the (non-staff-admin) user to sign out and try another
                  account; front-channel logout via Keycloak. */}
              <Button variant="outlined" onClick={() => logOut()}>
                تسجيل الخروج
              </Button>
              <AlertSystem />
            </div>
          </ThemeProvider>
        </CacheProvider>
      );
    }

    function MainContainer() {
      return (
        <div>
          <div>
            <IndexRouter
              isAdmin={isAuthorizedStaff}
              canAct={staffCanAct}
              tier={tier}
              adminOrgEnabled={adminOrgEnabled}
            />
          </div>
        </div>
      );
    }

    return (
      <CapabilitiesProvider>
        <CacheProvider value={cacheRtl}>
          <ThemeProvider theme={theme}>
            <div dir="rtl">
              <ScrollToTop path="/">
                <Router>
                  {isAuthorizedStaff && (
                    <NavBar
                      isAdmin={isAuthorizedStaff}
                      canAct={staffCanAct}
                      tier={tier}
                      adminOrgEnabled={adminOrgEnabled}
                    />
                  )}
                  <MainContainer />
                </Router>
              </ScrollToTop>
              <AlertSystem />
            </div>
          </ThemeProvider>
        </CacheProvider>
      </CapabilitiesProvider>
    );
  }

  return <AppContent />;
};
